/**
 * Work Triangle Calculation Service
 *
 * Implements the Kitchen Work Triangle principle:
 * - Sink ↔ Fridge ↔ Stove form a triangle
 * - Optimal perimeter: 4-8 meters
 * - Each leg: 1.2-2.7 meters
 * - No obstacles should cross the triangle paths
 */

import type {
  Point3D,
  LayoutPlacement,
  WorkTriangleMetrics,
  WorkTrianglePoint,
} from "@byk/shared";
import { WORK_TRIANGLE_OPTIMAL } from "@byk/shared";

// =============================================================================
// Distance Calculations
// =============================================================================

/**
 * Calculate Euclidean distance between two 3D points (ignores Z for floor distance)
 */
export function distance2D(a: Point3D, b: Point3D): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Get position for a specific role from placements
 */
function getPosition(
  placements: LayoutPlacement[],
  role: WorkTrianglePoint
): Point3D | null {
  const placement = placements.find((p) => p.role === role);
  return placement?.position ?? null;
}

// =============================================================================
// Triangle Metrics Calculation
// =============================================================================

/**
 * Calculate work triangle metrics from three placements
 */
export function calculateTriangleMetrics(
  placements: LayoutPlacement[]
): WorkTriangleMetrics {
  const sink = getPosition(placements, "sink");
  const fridge = getPosition(placements, "fridge");
  const cooktop = getPosition(placements, "cooktop");

  // If any point is missing, return invalid metrics
  if (!sink || !fridge || !cooktop) {
    return {
      perimeter: 0,
      sinkToFridge: 0,
      fridgeToCooktop: 0,
      cooktopToSink: 0,
      isOptimal: false,
      issues: ["Missing work triangle points"],
    };
  }

  // Calculate leg distances
  const sinkToFridge = distance2D(sink, fridge);
  const fridgeToCooktop = distance2D(fridge, cooktop);
  const cooktopToSink = distance2D(cooktop, sink);

  // Calculate perimeter
  const perimeter = sinkToFridge + fridgeToCooktop + cooktopToSink;

  // Validate against optimal ranges
  const issues: string[] = [];

  // Check perimeter
  if (perimeter < WORK_TRIANGLE_OPTIMAL.minPerimeter) {
    issues.push(
      `Perimeter too small (${Math.round(perimeter)}cm < ${WORK_TRIANGLE_OPTIMAL.minPerimeter}cm)`
    );
  }
  if (perimeter > WORK_TRIANGLE_OPTIMAL.maxPerimeter) {
    issues.push(
      `Perimeter too large (${Math.round(perimeter)}cm > ${WORK_TRIANGLE_OPTIMAL.maxPerimeter}cm)`
    );
  }

  // Check individual legs
  const legs = [
    { name: "Sink to Fridge", length: sinkToFridge },
    { name: "Fridge to Cooktop", length: fridgeToCooktop },
    { name: "Cooktop to Sink", length: cooktopToSink },
  ];

  for (const leg of legs) {
    if (leg.length < WORK_TRIANGLE_OPTIMAL.minLeg) {
      issues.push(
        `${leg.name} too short (${Math.round(leg.length)}cm < ${WORK_TRIANGLE_OPTIMAL.minLeg}cm)`
      );
    }
    if (leg.length > WORK_TRIANGLE_OPTIMAL.maxLeg) {
      issues.push(
        `${leg.name} too long (${Math.round(leg.length)}cm > ${WORK_TRIANGLE_OPTIMAL.maxLeg}cm)`
      );
    }
  }

  return {
    perimeter: Math.round(perimeter),
    sinkToFridge: Math.round(sinkToFridge),
    fridgeToCooktop: Math.round(fridgeToCooktop),
    cooktopToSink: Math.round(cooktopToSink),
    isOptimal: issues.length === 0,
    issues: issues.length > 0 ? issues : undefined,
  };
}

// =============================================================================
// Scoring
// =============================================================================

/**
 * Calculate a quality score (0-1) for a work triangle
 * Higher score = better layout
 */
export function calculateTriangleScore(metrics: WorkTriangleMetrics): number {
  if (!metrics.perimeter) return 0;

  let score = 1.0;

  // Penalize for being outside optimal perimeter range
  const { minPerimeter, maxPerimeter, minLeg, maxLeg } = WORK_TRIANGLE_OPTIMAL;
  const optimalPerimeter = (minPerimeter + maxPerimeter) / 2; // 600cm
  const perimeterDeviation =
    Math.abs(metrics.perimeter - optimalPerimeter) / optimalPerimeter;
  score -= perimeterDeviation * 0.3;

  // Penalize for legs outside optimal range
  const optimalLeg = (minLeg + maxLeg) / 2; // 195cm
  const legs = [
    metrics.sinkToFridge,
    metrics.fridgeToCooktop,
    metrics.cooktopToSink,
  ];

  for (const leg of legs) {
    const legDeviation = Math.abs(leg - optimalLeg) / optimalLeg;
    score -= legDeviation * 0.1;
  }

  // Bonus for being within optimal range
  if (metrics.isOptimal) {
    score += 0.1;
  }

  // Clamp to 0-1
  return Math.max(0, Math.min(1, score));
}

// =============================================================================
// Triangle Path Checking
// =============================================================================

/**
 * Check if a line segment between two points crosses any obstacles
 * Uses simple AABB collision for now
 */
export function doesPathCrossObstacle(
  from: Point3D,
  to: Point3D,
  obstacles: Array<{ x: number; y: number; width: number; depth: number }>
): boolean {
  // Simple line-rectangle intersection check
  for (const obstacle of obstacles) {
    if (
      lineIntersectsRect(
        from.x,
        from.y,
        to.x,
        to.y,
        obstacle.x,
        obstacle.y,
        obstacle.width,
        obstacle.depth
      )
    ) {
      return true;
    }
  }
  return false;
}

/**
 * Check if a line segment intersects a rectangle
 */
function lineIntersectsRect(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  rx: number,
  ry: number,
  rw: number,
  rh: number
): boolean {
  // Check if either endpoint is inside the rectangle
  if (pointInRect(x1, y1, rx, ry, rw, rh)) return true;
  if (pointInRect(x2, y2, rx, ry, rw, rh)) return true;

  // Check if line intersects any edge of the rectangle
  const edges = [
    { x1: rx, y1: ry, x2: rx + rw, y2: ry }, // Top
    { x1: rx + rw, y1: ry, x2: rx + rw, y2: ry + rh }, // Right
    { x1: rx, y1: ry + rh, x2: rx + rw, y2: ry + rh }, // Bottom
    { x1: rx, y1: ry, x2: rx, y2: ry + rh }, // Left
  ];

  for (const edge of edges) {
    if (lineIntersectsLine(x1, y1, x2, y2, edge.x1, edge.y1, edge.x2, edge.y2)) {
      return true;
    }
  }

  return false;
}

/**
 * Check if a point is inside a rectangle
 */
function pointInRect(
  px: number,
  py: number,
  rx: number,
  ry: number,
  rw: number,
  rh: number
): boolean {
  return px >= rx && px <= rx + rw && py >= ry && py <= ry + rh;
}

/**
 * Check if two line segments intersect
 */
function lineIntersectsLine(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  x3: number,
  y3: number,
  x4: number,
  y4: number
): boolean {
  const denom = (y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1);
  if (Math.abs(denom) < 0.0001) return false; // Parallel lines

  const ua = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denom;
  const ub = ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) / denom;

  return ua >= 0 && ua <= 1 && ub >= 0 && ub <= 1;
}

// =============================================================================
// Triangle Centroid
// =============================================================================

/**
 * Calculate the centroid (center point) of a work triangle
 * Useful for positioning labels or UI elements
 */
export function getTriangleCentroid(placements: LayoutPlacement[]): Point3D {
  const sink = getPosition(placements, "sink");
  const fridge = getPosition(placements, "fridge");
  const cooktop = getPosition(placements, "cooktop");

  if (!sink || !fridge || !cooktop) {
    return { x: 0, y: 0, z: 0 };
  }

  return {
    x: (sink.x + fridge.x + cooktop.x) / 3,
    y: (sink.y + fridge.y + cooktop.y) / 3,
    z: 0,
  };
}
